// src/com/auto_com_veh_info/AutoComVehInfoInput.js
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import {
    Button, DialogContent, DialogActions, Grid, CircularProgress,
} from '@mui/material';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { AppWord, AppObject, AppStyle } from '../../value/AppConst';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import DraggableEditor from '../../common/DraggableEditor';
import VzmTextField from '../../common/VzmTextField';
import { ErrorHintBox, VzmPrint, VzmLabel } from '../../common/VzmStyled';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import InputUtil from '../../value/InputUtil';
import { AutoComVehInfoDictionary } from './AutoComVehInfoDict';
import { useAlertSetter } from '../../api/app-hook';
import { userInfoRepo, vehTypeGroupOrderAllListRepo } from '../../value/AppModel';
import { LIST_VEH_TYPE_GROUP_ORDER_ALL } from '../veh_type_group_order/VehTypeGroupOrderGql'; // 그룹에 속하는 모든 차종 목록

const InputMap = AutoComVehInfoDictionary.inputMap;
const defaultInputData = InputUtil.defaultValuesFromInputMap(InputMap);

const TypeList = styled(Box)({fontSize: '0.8rem'});
const TypeItem = styled('div')({
    display: 'flex', alignItems: 'center', padding: '2px 10px 2px 10px',
    '&:hover': {backgroundColor: '#f0f0f0'}
});

const TypeButtonBox = styled(Box)({display: 'flex', alignItems: 'center', flexGrow: 1});
const TypeButton = styled(Button)({textTransform: 'none',});
const TypeIdLabel = styled('div')({color:'#777', width:90, textAlign:'center'}); // pallete color
const CapaLabel = styled('div')({color:'#777', width:60}); // pallete color

const InfoBox = styled(Box)({padding:10});
/**
 * (AUTO_COM_VEH_INFO) 정보 생성 또는 편집 Dialog
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-10-22 16:15:21 using DbdbTap
 */
export default function AutoComVehInfoInput({
    open,
    comInfo,
    currentList,
    item,
    onClose,
    onRequestSave,
    responseSaving, // Response if result is NOT OK.
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const [input, setInput] = useState({...AppObject.emptyInputData});
    const [isEdit, setIsEdit] = useState(false);
    const [dupMessage, setDupMessage] = useState(null);
    const vehTypeGroupOrderAllLisData = useReactiveVar(vehTypeGroupOrderAllListRepo);
    const [vehTypesOfDc, setVehTypesOfDc] = useState([]);
    const openConfirmDialog = useConfirmDialog();
    const addAlert = useAlertSetter();
    const {loading:loadingSave, error:errorSave, data:resultSave} = responseSaving; // 전송 결과에 대한 View 기능 소화.

    const [getVehTypeGroupOrderAllList, responseVehTypeGroupOrderAllList] = useLazyQuery(LIST_VEH_TYPE_GROUP_ORDER_ALL, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetVehTypeGroupOrderAllList(data, option)},
        onError: (error) => addAlert({title: '차종목록조회 오류', message: [error.message]})
    });

    // const dcTypeCdOptions = ValueMisc.makeCodesToSelectList(codeTree, CodeGroup.DC_TYPE_CD, InputMap.dcTypeCd.id);

    useEffect(()=>{
        if(open) {
            const inputSet = {...AppObject.emptyInputData};
            let editing = false;
            if(item) {
                inputSet.data = {...item};
                editing = true;
            }
            else {
                inputSet.data = {...defaultInputData};
                inputSet.data.dcCd = comInfo ? comInfo.dcCd : null;
                inputSet.data.comId = comInfo ? comInfo.comId : null;
            }
            setInput(inputSet);
            setIsEdit(editing);
        }
    }, [open, item]);

    useEffect(()=>{
        if(open) {
            const diff = new Date().getTime()/1000 - vehTypeGroupOrderAllLisData.when;
            if(diff > 600) { //  || vehTypeGroupOrderAllLisData.records.length === 0
                getVehTypeGroupOrderAllList();
            }
            else {
                const dcCd = comInfo ? comInfo.dcCd : sessionInfo.user.getDcCd();
                const vehTypes = vehTypeGroupOrderAllLisData.records.filter((item)=>item.dcCd === dcCd);
                setVehTypesOfDc(vehTypes);
            }
        }
    }, [open, vehTypeGroupOrderAllLisData]);

    useEffect(()=>{
        if(open && !isEdit && !input.hasError && input.changed) {
            const other = currentList.find((item)=>{
                return item.vehTypeId === input.data.vehTypeId
                    && item.comId === input.data.comId
                    && item.dcCd === input.data.dcCd;
            });
            if(other) {
                setDupMessage('이미 등록된 차종입니다.');
            }
            else {
                setDupMessage(null);
            }
        }
        else {
            setDupMessage(null);
        }
    }, [open, isEdit, input, currentList]);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetVehTypeGroupOrderAllList = (data, option) => {
        if(data.vehTypeGroupOrderListAll) {
            vehTypeGroupOrderAllListRepo({
                when: new Date().getTime()/1000,
                records: data.vehTypeGroupOrderListAll
            });
        }
    };

    // >>>>>>>>> Events <<<<<<<<<<<<<
    const onChangeValue = (id, value, error) => {
        const newInput = InputUtil.reflectData(input, id, value, error, InputMap, isEdit);
        setInput(newInput);
    };

    const onClickSubmit = () => {
        const param = InputUtil.getDataToSubmit(input.data, InputMap, isEdit);
        onRequestSave(param, isEdit);
    };

    const askCancel = async () => {
        const confirm = await openConfirmDialog({
            ...AppObject.messageCancelEdit,
            option: {yesLabel: AppWord.IgnoreAndClose}
        });
        if(confirm) onClose();
    };

    const onClickCancel = () => {
        if(input.changed) askCancel();
        else onClose();
    };

    const loadingAny = loadingSave || responseVehTypeGroupOrderAllList.loading;

    const renderTypeList = () => {
        return(
            <Box>
                <Box
                    style={{
                        fontSize: '0.7rem', color: AppStyle.PrimaryRGB, marginLeft: 12
                    }}
                >
                    차종 선택
                </Box>
                <Box style={{
                    height:'30vh', overflow:'auto', border: AppStyle.BorderCCC, borderRadius:5,
                    paddingTop: 5,
                    }}
                >
                    <TypeList style={{fontSize: '0.8rem'}}>
                        {
                            vehTypesOfDc.map((item) => {
                                return(
                                    <TypeItem key={item.vehTypeId}
                                        sx={{ py: 0, height: 32}}
                                    >
                                        <TypeButtonBox>
                                            <TypeButton
                                                variant={AppStyle.mui.text}
                                                startIcon={
                                                    item.vehTypeId === input.data.vehTypeId
                                                    ? <RadioButtonCheckedIcon />
                                                    : <RadioButtonUncheckedIcon />
                                                }
                                                onClick={()=>{
                                                    onChangeValue(InputMap.vehTypeId.id, item.vehTypeId);
                                                }}
                                            >
                                                {item.vehTypeName}
                                            </TypeButton>
                                        </TypeButtonBox>
                                        <TypeIdLabel>
                                            코드 {item.vehTypeId}
                                        </TypeIdLabel>
                                        <CapaLabel>
                                            {item.loadCapa ? item.loadCapa : '-'} PLT
                                        </CapaLabel>
                                    </TypeItem>
                                )
                            })
                        }
                    </TypeList>
                </Box>
            </Box>
        );
    };

    // Input field initiallized using getInputField(c,t,i)
    return(
        <DraggableEditor open={open} maxWidth='sm'
            onClose={onClickCancel}
            title={isEdit ? '운송사 차종 정보 수정' : '운송사 차종 정보 등록'}
        >
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <VzmPrint
                            label='운송사'
                            value={comInfo ? comInfo.comName : ''}
                        />
                    </Grid>
                    <Grid item xs={7}>
                        {
                            isEdit ?
                            <VzmPrint
                                label="차종"
                                value={item ? item.vehTypeName : '[App 오류]'}
                            />
                            :
                            renderTypeList()
                        }
                    </Grid>
                    <Grid item xs={5}>
                        <Box style={{padding: 10, fontSize:'0.85rem'}}>
                            {
                                isEdit ? null :
                                <Box>
                                    <VzmLabel>현재 등록된 차종</VzmLabel>
                                    <InfoBox>{comInfo.comName}: {currentList.length}개</InfoBox>
                                </Box>
                            }
                            {
                                dupMessage &&
                                <>
                                    <VzmLabel>중복 확인</VzmLabel>
                                    <InfoBox>{dupMessage}</InfoBox>
                                </>
                            }
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <VzmTextField
                            dict={InputMap.vehTypeAlias}
                            value={input.data.vehTypeAlias}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                
                </Grid>
            </DialogContent>
            <DialogActions style={{display:'flex'}}>
                {
                    input.errorLabels && input.errorLabels.length > 0
                    ?
                    <ErrorHintBox>
                        확인: {input.errorLabels.join(', ')}
                    </ErrorHintBox>
                    : null
                }
                {
                    loadingSave
                    ?
                    <span>
                        <CircularProgress />
                        &nbsp;데이터 저장 중...
                    </span>
                    :
                    <>
                        <Button
                            onClick={onClickSubmit}
                            disabled={input.hasError || !input.changed || loadingAny}
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                        >저장</Button>
                        <Button onClick={onClickCancel}
                            color={AppStyle.mui.warning}
                            variant={AppStyle.mui.contained}
                        >취소</Button>
                    </>
                }
            </DialogActions>
        </DraggableEditor>
    );
}
